import React, {useState } from 'react'
import { useDispatch } from 'react-redux'
import Box from '@mui/system/Box'
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Form, Formik } from 'formik';
import clsx from 'clsx';
import InputField from '../../../../../common/InputField';
import jwtAxios from '../../../../../services/axiosInstance/ApiConfig';
import { makeStyles } from '@mui/styles';
import { showErrorMessage, showSuccessMessage } from '../../../../../common/Toaster';
import { getHeaders } from '../../../../../store/actions/Auth';
import { editUserSchema } from '../../../../../schemas/editUser.schema';
import Button from '@mui/material/Button';
import { SET_UPLOAD_URL } from '../../../../../store/types/actions/ProductAction';
import { onImageUpload } from '../../../../../store/actions/uploadAction';
import UploadImage from '../../../../../common/UploadImage';
import { useCommonStyles } from '../../../../../common/common';
import { BooleanValueTypes } from '../../../../../constants/AppConst';
import CustomRadio from '../../../../../common/CustomRadio';

const useStyles = makeStyles({
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center'
	}
});

interface EditProfileProps {
	user: any;
}

interface EditProfileState {
	name: string;
	email: string;
	isSeller: boolean;
	sellerName?: string;
	sellerDescription?: string;
	sellerLogo?: string;
	rating?: string;
	numReviews?: string;
}

const EditProfile = (props: EditProfileProps) => {
	const classes: any = useStyles(props);
	const { user } = props;
	const getInitialValues = (user: any) => {
		if(user) {
			if(user.isSeller) {
				return {
					name: user.name,
					email: user.email,
					isSeller: user.isSeller,
					sellerName: user.seller && user.seller.name,
					sellerDescription: user.seller && user.seller.description,
					sellerLogo: user.seller && user.seller.logo,
					rating: user.seller && user.seller.rating,
					numReviews: user.seller && user.seller.numReviews
				}
			} else {
				return {
					name: user.name,
					email: user.email,
					isSeller: user.isSeller
				}
			}
		} else {
			return {
				name: '',
				email: '',
				isSeller: false
			}
		}
	}

	const [values, setValues] = useState<EditProfileState>(getInitialValues(user))
	const [isEdit, setIsEdit] = useState(false)
	const [addSeller, setAddSeller] = useState(false)
	const [currentUserToSeller, setCurrentUserToSeller] = useState(false)
	const dispatch = useDispatch()
	const commonClasses = useCommonStyles()

	const submitHandler = (data: EditProfileState, setSubmitting: any) => {
		let payload = {
			...data,
			// image: imageUrl,
			isSeller: currentUserToSeller
		}
		console.log("payload : ", payload)
		setSubmitting(true);
		jwtAxios.post('/products/addProduct', payload, { headers: getHeaders() })
			.then(response => {
				showSuccessMessage(response.data.message)
			}).catch((err: any) => showErrorMessage(err))
		setSubmitting(false);
	}

	const handleImageUpload = (event: any) => {
		dispatch({ type: SET_UPLOAD_URL, payload: null })
		const file = event.target.files[0];
		const bodyFormData = new FormData();
		bodyFormData.append('image', file);
		dispatch(onImageUpload(bodyFormData))
	}

	const onClickEditSeller = () => {
		setIsEdit(true)
	}

	const onClose = (close: boolean) => {
		setIsEdit(close)
	}

	const handleAddSellerOnClick = (flag: boolean) => {
		setAddSeller(flag)
		setValues({ ...values, isSeller: flag })
	}

	const handleRadioSelect = (event: any) => {
		setCurrentUserToSeller(event.target.value)
	}

	return (
		<Box display="flex" flexDirection="column" p={2}>
			<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
				<Grid item sm={1}></Grid>
				<Grid item sm={10}>
					<Box component="h4"> {user && user.name} Profile </Box>
					<Paper className={classes.paper}>
						<Box flex={1} display='flex' p={2} flexDirection='column'>
							<Formik
								validateOnChange={true}
								initialValues={{ ...values }}
								validationSchema={editUserSchema}
								onSubmit={(data, { setSubmitting }) => submitHandler(data, setSubmitting)}>
								{({ isSubmitting }) => (
									<Form noValidate autoComplete="off">
										<Box mb={{ xs: 2, xl: 3 }}>
											<InputField
												required
												id="user-name"
												placeholder='user name'
												name='name'
												label='Your name'
												variant='outlined'
											/>
										</Box>
										<Box mb={{ xs: 3, xl: 3 }}>
											<InputField
												required
												id="user-email"
												placeholder='your email'
												name='email'
												label='Email'
												disabled={true}
												variant='outlined'
											/>
										</Box>
										{!values.isSeller &&
											<Box display="flex" justifyContent="center">
												<Button
													color="primary"
													variant="contained"
													className={clsx(classes.signInButton)}
													onClick={() => handleAddSellerOnClick(true)}
												>
													Add Seller Account
												</Button>
											</Box>
										}
										{values.isSeller &&
											<Box mt={2} mb={2}>
												<Box mb={{ xs: 3, xl: 3 }}>
													<Box component="h5" className={commonClasses.d_f_row_spc_btwn}>
														Seller details
														{!addSeller && <Button
															className={clsx(classes.signInButton)}
															onClick={() => onClickEditSeller()}
															style={{ float: 'right', textTransform: 'capitalize' }}
														>
															Edit
														</Button>}
														{addSeller && <Button
															className={clsx(classes.signInButton)}
															onClick={() => addSeller ? handleAddSellerOnClick(false) : onClose(false)}
															style={{ float: 'right', textTransform: 'capitalize' }}
														>
															Close
														</Button>}
													</Box>
												</Box>
												{(isEdit || addSeller) && <>
													<Box mb={{ xs: 1, xl: 2 }}>
														<CustomRadio
															options={BooleanValueTypes}
															legend={addSeller && !isEdit ? "Would you like to use your current account as a seller account?" : ""}
															defaultValue={isEdit ? values.isSeller : addSeller ? '' : false}
															handleChange={handleRadioSelect}
														/>
													</Box>
													<Box mb={{ xs: 3, xl: 3 }}>
														<InputField
															required
															id="seller-name"
															placeholder='seller name'
															name='sellerName'
															label='Seller name'
															variant='outlined'
														/>
													</Box>
													<Box mb={{ xs: 1, xl: 3 }}>
														<UploadImage
															onChange={handleImageUpload}
															imageUrl={user && user.logo}
														/>
													</Box>
													<Box mb={{ xs: 2, xl: 3 }}>
														<InputField
															required
															id="seller-description"
															placeholder='seller description'
															name='sellerDescription'
															label='Description'
															variant='outlined'
														/>
													</Box>
													<Box mb={{ xs: 2, xl: 3 }}>
														<InputField
															required
															id="seller-rating"
															placeholder='Rating'
															name='rating'
															label='Rating'
															variant='outlined'
														/>
													</Box>
													<Box mb={{ xs: 2, xl: 3 }}>
														<InputField
															required
															id="seller-reviews"
															placeholder='Total Reviews'
															name='numReviews'
															label='Total reviews'
															variant='outlined'
														/>
													</Box>
												</>}
											</Box>
										}
										{(isEdit || addSeller) &&
											<Box
												mb={3}
												display='flex'
												flexDirection={{ xs: 'column', sm: 'row' }}
												justifyContent='space-between'>
												<Button
													color="secondary"
													variant="outlined"
													className={clsx(classes.signInButton)}
													onClick={() => onClose(false)}
												>
													Cancel
												</Button>
												<Button
													color="primary"
													variant="contained"
													type="submit"
													disabled={isSubmitting}
													className={clsx(classes.signInButton)}>
													Save
												</Button>
											</Box>
										}
									</Form>
								)}
							</Formik>
						</Box>
					</Paper>
				</Grid>
				<Grid item sm={1}></Grid>
			</Grid>
		</Box>
	)
}

export default EditProfile;
